// Cache utilities for improving application performance

// Cache configuration
const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const MAX_CACHE_SIZE = 50; // Maximum number of items to store in cache
const CACHE_PREFIX = 'masterymint_';

// Cache types
export interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

// Cache storage interface
export interface CacheStorage {
  get<T>(key: string): CacheItem<T> | null;
  set<T>(key: string, item: CacheItem<T>): void;
  remove(key: string): void;
  clear(): void;
  keys(): string[];
}

// Local storage implementation of cache storage
export class LocalStorageCache implements CacheStorage {
  get<T>(key: string): CacheItem<T> | null {
    try {
      const item = localStorage.getItem(`${CACHE_PREFIX}${key}`);
      if (!item) return null;
      return JSON.parse(item) as CacheItem<T>;
    } catch (error) {
      console.error('Error retrieving from cache:', error);
      return null;
    }
  }

  set<T>(key: string, item: CacheItem<T>): void {
    try {
      // Ensure we don't exceed the cache size limit
      this.enforceMaxSize();
      localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(item));
    } catch (error) {
      console.error('Error setting cache item:', error);
      // If we hit storage limits, clear older items
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.clearOldItems();
        try {
          localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(item));
        } catch (retryError) {
          console.error('Failed to set cache item after clearing old items:', retryError);
        }
      }
    }
  }

  remove(key: string): void {
    localStorage.removeItem(`${CACHE_PREFIX}${key}`);
  }

  clear(): void {
    const allKeys = this.keys();
    allKeys.forEach(key => {
      localStorage.removeItem(`${CACHE_PREFIX}${key}`);
    });
  }

  keys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keys.push(key.substring(CACHE_PREFIX.length));
      }
    }
    return keys;
  }

  private enforceMaxSize(): void {
    const keys = this.keys();
    if (keys.length >= MAX_CACHE_SIZE) {
      this.clearOldItems();
    }
  }

  private clearOldItems(): void {
    const keys = this.keys();
    const items: { key: string; timestamp: number }[] = [];

    // Collect all items with their timestamp
    keys.forEach(key => {
      const item = this.get(key);
      if (item) {
        items.push({ key, timestamp: item.timestamp });
      }
    });

    // Sort by timestamp (oldest first)
    items.sort((a, b) => a.timestamp - b.timestamp);

    // Remove the oldest 25% of items
    const itemsToRemove = Math.ceil(items.length * 0.25);
    items.slice(0, itemsToRemove).forEach(item => {
      this.remove(item.key);
    });
  }
}

// Memory cache implementation (for environments without localStorage)
export class MemoryCache implements CacheStorage {
  private cache: Map<string, any> = new Map();

  get<T>(key: string): CacheItem<T> | null {
    return this.cache.get(key) || null;
  }

  set<T>(key: string, item: CacheItem<T>): void {
    if (this.cache.size >= MAX_CACHE_SIZE) {
      this.clearOldItems();
    }
    this.cache.set(key, item);
  }

  remove(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  private clearOldItems(): void {
    const items: { key: string; timestamp: number }[] = [];
    
    this.cache.forEach((value, key) => {
      items.push({ key, timestamp: value.timestamp });
    });
    
    items.sort((a, b) => a.timestamp - b.timestamp);
    
    const itemsToRemove = Math.ceil(items.length * 0.25);
    items.slice(0, itemsToRemove).forEach(item => {
      this.cache.delete(item.key);
    });
  }
}

// Create the appropriate cache based on environment
export const createCache = (): CacheStorage => {
  if (typeof window !== 'undefined' && window.localStorage) {
    return new LocalStorageCache();
  }
  return new MemoryCache();
};

// The cache instance
export const cache = createCache();

// Main cache API
export const cacheData = <T>(key: string, data: T, ttl: number = DEFAULT_CACHE_TTL): void => {
  const now = Date.now();
  cache.set(key, {
    data,
    timestamp: now,
    expiresAt: now + ttl
  });
};

export const getCachedData = <T>(key: string): T | null => {
  const item = cache.get<T>(key);
  
  if (!item) return null;
  
  // Check if the item has expired
  if (Date.now() > item.expiresAt) {
    cache.remove(key);
    return null;
  }
  
  return item.data;
};

export const removeCachedData = (key: string): void => {
  cache.remove(key);
};

export const clearCache = (): void => {
  cache.clear();
};

export const invalidateCache = (pattern: string | RegExp): void => {
  const keys = cache.keys();
  
  keys.forEach(key => {
    if (typeof pattern === 'string') {
      if (key.includes(pattern)) {
        cache.remove(key);
      }
    } else if (pattern.test(key)) {
      cache.remove(key);
    }
  });
};

// Function to get cached data or fetch from API
export const getCachedOrFetch = async <T>(
  key: string,
  fetchFn: () => Promise<T>,
  ttl: number = DEFAULT_CACHE_TTL
): Promise<T> => {
  // Try to get from cache first
  const cachedData = getCachedData<T>(key);
  if (cachedData !== null) {
    return cachedData;
  }
  
  // If not in cache, fetch the data
  try {
    const data = await fetchFn();
    cacheData(key, data, ttl);
    return data;
  } catch (error) {
    console.error(`Error fetching data for key ${key}:`, error);
    throw error;
  }
};

// Function to prefetch and cache data
export const prefetchData = <T>(
  key: string,
  fetchFn: () => Promise<T>,
  ttl: number = DEFAULT_CACHE_TTL
): Promise<void> => {
  return new Promise((resolve) => {
    // Only prefetch if not already in cache
    if (getCachedData<T>(key) === null) {
      fetchFn()
        .then(data => {
          cacheData(key, data, ttl);
          resolve();
        })
        .catch(error => {
          console.error(`Error prefetching data for key ${key}:`, error);
          resolve();
        });
    } else {
      resolve();
    }
  });
};

// Function to update cached data
export const updateCachedData = <T>(
  key: string,
  updateFn: (oldData: T | null) => T,
  ttl: number = DEFAULT_CACHE_TTL
): void => {
  const oldData = getCachedData<T>(key);
  const newData = updateFn(oldData);
  cacheData(key, newData, ttl);
};